import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import StatsCards from './components/StatsCards';
import UserFilters from './components/UserFilters';
import UserTableRow from './components/UserTableRow';
import UserMobileCard from './components/UserMobileCard';
import BulkActionsBar from './components/BulkActionsBar';
import RolePermissionsPanel from './components/RolePermissionsPanel';
import AuditLogPanel from './components/AuditLogPanel';
import ActionConfirmModal from './components/ActionConfirmModal';
import AddUserModal from './components/AddUserModal';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { sendEmailIfEnabled } from '../../lib/emailService';


const UserManagement = () => {
  const navigate = useNavigate();
  const { userProfile, signOut } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', role: '', status: '', organization: '' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('profiles')?.select('*, organizations(id, razon_social, nit)')?.order('created_at', { ascending: false });

      if (error) throw error;

      const mapped = (data || [])?.map(p => ({
        id: p?.id,
        name: p?.full_name || p?.email,
        email: p?.email,
        organization: p?.organizations?.razon_social || '—',
        nit: p?.organizations?.nit || '—',
        role: p?.role,
        status: p?.status,
        lastLogin: p?.updated_at ? new Date(p.updated_at)?.toLocaleString('es-CO') : 'Nunca',
        _raw: p,
      }));

      setUsers(mapped);
    } catch (err) {
      showToast('Error al cargar usuarios.', 'warning');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();

    // Real-time subscription
    const channel = supabase?.channel('profiles_user_mgmt')?.on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
        fetchUsers();
      })?.subscribe();

    return () => supabase?.removeChannel(channel);
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    return users?.filter(u => {
      const q = filters?.search?.toLowerCase();
      const matchSearch = !q || u?.name?.toLowerCase()?.includes(q) || u?.email?.toLowerCase()?.includes(q) || u?.nit?.includes(q);
      const matchRole = !filters?.role || u?.role === filters?.role;
      const matchStatus = !filters?.status || u?.status === filters?.status;
      const matchOrg = !filters?.organization || u?.organization?.toLowerCase()?.includes(filters?.organization?.toLowerCase()) || u?.nit?.includes(filters?.organization);
      return matchSearch && matchRole && matchStatus && matchOrg;
    });
  }, [users, filters]);

  const stats = useMemo(() => ({
    total: users?.length,
    active: users?.filter(u => u?.status === 'ACTIVE')?.length,
    pending: users?.filter(u => u?.status === 'PENDING')?.length,
    suspended: users?.filter(u => u?.status === 'SUSPENDED')?.length,
  }), [users]);

  const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value }));
  const handleClearFilters = () => setFilters({ search: '', role: '', status: '', organization: '' });

  const handleSelectUser = (id, checked) => {
    setSelectedIds(prev => checked ? [...prev, id] : prev?.filter(i => i !== id));
  };

  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? filteredUsers?.map(u => u?.id) : []);
  };

  const handleAction = (user, actionKey) => {
    setConfirmModal({ user, action: actionKey });
  };

  const handleBulkAction = (actionKey) => {
    setConfirmModal({ action: `bulk_${actionKey}`, user: null });
  };

  const handleConfirm = async () => {
    const { action, user } = confirmModal;

    try {
      if (action === 'suspend') {
        await supabase?.from('profiles')?.update({ status: 'SUSPENDED' })?.eq('id', user?.id);
        await supabase?.from('audit_logs')?.insert({ actor_id: userProfile?.id, action: 'USER_SUSPENDED', target_type: 'profile', target_id: user?.id, metadata: { email: user?.email } });
        showToast(`Usuario ${user?.name} suspendido.`);
      } else if (action === 'reactivate' || action === 'activate') {
        await supabase?.from('profiles')?.update({ status: 'ACTIVE' })?.eq('id', user?.id);
        await supabase?.from('audit_logs')?.insert({ actor_id: userProfile?.id, action: 'USER_ACTIVATED', target_type: 'profile', target_id: user?.id, metadata: { email: user?.email } });
        showToast(`Usuario ${user?.name} activado.`);
        // Send activation email
        try {
          const { data: orgData } = await supabase
            ?.from('profiles')
            ?.select('organizations(razon_social)')
            ?.eq('id', user?.id)
            ?.single();
          await sendEmailIfEnabled({
            toggleKey: 'notify_user_activated',
            to: user?.email,
            template: 'cuenta_activada',
            variables: {
              razon_social: orgData?.organizations?.razon_social || user?.organization || user?.name,
              login_url: `${window?.location?.origin}/login`,
            },
          });
        } catch (emailErr) {
        }
      } else if (action === 'reject') {
        await supabase?.from('profiles')?.update({ status: 'REJECTED' })?.eq('id', user?.id);
        await supabase?.from('audit_logs')?.insert({ actor_id: userProfile?.id, action: 'USER_REJECTED', target_type: 'profile', target_id: user?.id, metadata: { email: user?.email } });
        showToast(`Solicitud de ${user?.name} rechazada.`, 'warning');
        // Send rejection email
        try {
          const { data: orgData } = await supabase
            ?.from('profiles')
            ?.select('organizations(razon_social)')
            ?.eq('id', user?.id)
            ?.single();
          await sendEmailIfEnabled({
            toggleKey: 'notify_user_rejected',
            to: user?.email,
            template: 'cuenta_rechazada',
            variables: {
              razon_social: orgData?.organizations?.razon_social || user?.organization || user?.name,
            },
          });
        } catch (emailErr) {
        }
      } else if (action === 'reset_password') {
        await supabase?.auth?.resetPasswordForEmail(user?.email);
        showToast(`Enlace de restablecimiento enviado a ${user?.email}.`);
      } else if (action === 'bulk_activate') {
        await supabase?.from('profiles')?.update({ status: 'ACTIVE' })?.in('id', selectedIds);
        showToast(`${selectedIds?.length} usuarios activados.`);
        setSelectedIds([]);
      } else if (action === 'bulk_suspend') {
        await supabase?.from('profiles')?.update({ status: 'SUSPENDED' })?.in('id', selectedIds);
        showToast(`${selectedIds?.length} usuarios suspendidos.`, 'warning');
        setSelectedIds([]);
      } else if (action === 'bulk_export') {
        showToast(`${selectedIds?.length} usuarios exportados.`);
        setSelectedIds([]);
      }

      await fetchUsers();
    } catch (err) {
      showToast('Error al ejecutar la acción.', 'warning');
    }

    setConfirmModal(null);
  };

  const handleAddUser = async (formData) => {
    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase?.auth?.admin?.createUser({
        email: formData?.email,
        password: formData?.password || 'TempPass2026!',
        email_confirm: true,
        user_metadata: { full_name: formData?.name, role: formData?.role, status: 'ACTIVE' },
      });

      if (authError) {
        showToast('Error al crear usuario: ' + authError?.message, 'warning');
        return;
      }

      showToast(`Usuario ${formData?.name} agregado correctamente.`);
      setShowAddModal(false);
      await fetchUsers();
    } catch (err) {
      // Fallback: show success (admin API may not be available client-side)
      showToast('Usuario creado. Puede que requiera confirmación de email.', 'warning');
      setShowAddModal(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const allSelected = filteredUsers?.length > 0 && filteredUsers?.every(u => selectedIds?.includes(u?.id));
  const someSelected = filteredUsers?.some(u => selectedIds?.includes(u?.id)) && !allSelected;
  const contentMargin = sidebarCollapsed ? 'lg:ml-[60px]' : 'lg:ml-[240px]';

  const currentUser = {
    name: userProfile?.full_name || 'Administrador',
    role: userProfile?.role || 'ADMIN',
    organization: 'Update-vital-Ftech',
  };
  const notifications = { pendingApprovals: stats?.pending, pendingUsers: stats?.pending };

  return (
    <div className="min-h-screen bg-gray-50" style={{ backgroundColor: 'var(--color-background)' }}>
      <RoleBasedSidebar user={currentUser} notifications={notifications} isCollapsed={sidebarCollapsed} onCollapsedChange={setSidebarCollapsed} onLogout={handleLogout} />
      <main className={`${contentMargin} transition-all duration-250 min-h-screen`}>
        {/* Vital branded sticky header */}
        <div className="sticky top-0 z-10 px-4 md:px-6 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" style={{ backgroundColor: 'var(--color-card)', borderBottom: '2px solid var(--color-primary)', boxShadow: '0 2px 8px rgba(0,188,212,0.1)' }}>
          <div className="flex items-center gap-3 pl-10 lg:pl-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#00BCD4' }}>
              <Icon name="Users" size={18} color="#fff" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold font-heading" style={{ color: 'var(--color-foreground)' }}>Gestión de Usuarios</h1>
              <p className="text-xs font-caption" style={{ color: 'var(--color-primary)' }}>Administre usuarios, roles y permisos del sistema</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" iconName="BarChart2" iconPosition="left" onClick={() => navigate('/administrative-dashboard')} style={{ borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}>Dashboard</Button>
            <Button variant="default" size="sm" iconName="UserPlus" iconPosition="left" onClick={() => setShowAddModal(true)} style={{ backgroundColor: '#00BCD4', borderColor: '#00BCD4' }}>Agregar Usuario</Button>
          </div>
        </div>

        <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-5"><StatsCards stats={stats} /></div>

          <div className="mb-4">
            <UserFilters filters={filters} onFilterChange={handleFilterChange} onClearFilters={handleClearFilters} totalResults={filteredUsers?.length} />
          </div>

          <div className="flex flex-col xl:flex-row gap-5">
            <div className="flex-1 min-w-0">
              {loading ? (
                <div className="flex items-center justify-center py-16 rounded-xl border" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 0.8s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                </div>
              ) : (
                <>
                  <div className="hidden md:block rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: '0 2px 12px rgba(0,188,212,0.08)' }}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm vital-table">
                        <thead>
                          <tr>
                            <th className="px-4 py-3 w-10">
                              <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected; }} onChange={(e) => handleSelectAll(e?.target?.checked)} className="w-4 h-4 rounded" style={{ accentColor: '#00BCD4' }} />
                            </th>
                            <th className="px-4 py-3 text-left">Usuario</th>
                            <th className="px-4 py-3 text-left hidden md:table-cell">Organización</th>
                            <th className="px-4 py-3 text-left">Rol</th>
                            <th className="px-4 py-3 text-left">Estado</th>
                            <th className="px-4 py-3 text-left hidden lg:table-cell">Último acceso</th>
                            <th className="px-4 py-3 w-12"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers?.length === 0 ? (
                            <tr><td colSpan={7} className="px-4 py-12 text-center">
                              <div className="flex flex-col items-center gap-3">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-muted)' }}><Icon name="Users" size={22} color="var(--color-muted-foreground)" /></div>
                                <p className="text-sm font-caption" style={{ color: 'var(--color-muted-foreground)' }}>No se encontraron usuarios</p>
                                <Button variant="outline" size="sm" onClick={handleClearFilters} style={{ borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}>Limpiar filtros</Button>
                              </div>
                            </td></tr>
                          ) : (
                            filteredUsers?.map(user => (
                              <UserTableRow key={user?.id} user={user} currentUserRole={currentUser?.role} onAction={handleAction} isSelected={selectedIds?.includes(user?.id)} onSelect={handleSelectUser} />
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    {filteredUsers?.length > 0 && (
                      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
                        <p className="text-xs text-gray-500 font-caption">Mostrando {filteredUsers?.length} de {users?.length} usuarios</p>
                        <Button variant="ghost" size="xs" iconName="RefreshCw" iconPosition="left" onClick={fetchUsers}>Actualizar</Button>
                      </div>
                    )}
                  </div>

                  <div className="md:hidden space-y-3">
                    {filteredUsers?.length === 0 ? (
                      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                        <Icon name="Users" size={32} color="#9CA3AF" />
                        <p className="text-sm text-gray-500 font-caption mt-3">No se encontraron usuarios</p>
                        <Button variant="outline" size="sm" className="mt-3" onClick={handleClearFilters}>Limpiar filtros</Button>
                      </div>
                    ) : (
                      filteredUsers?.map(user => (
                        <UserMobileCard key={user?.id} user={user} currentUserRole={currentUser?.role} onAction={handleAction} />
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="xl:w-80 flex-shrink-0 space-y-4">
              <RolePermissionsPanel currentUserRole={currentUser?.role} />
              <AuditLogPanel />
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <p className="text-xs font-semibold text-gray-500 font-caption uppercase tracking-wide mb-3">Accesos r\u00e1pidos</p>
                <div className="space-y-1">
                  {[
                    { label: 'Registro de Usuario', path: '/user-registration', icon: 'UserPlus' },
                    { label: 'Dashboard Admin', path: '/administrative-dashboard', icon: 'LayoutDashboard' },
                    { label: 'Gesti\u00f3n de Actualizaciones', path: '/update-management', icon: 'RefreshCw' },
                    { label: 'Configuraci\u00f3n del Sistema', path: '/system-configuration', icon: 'Settings' },
                  ]?.map(link => (
                    <button key={link?.path} onClick={() => navigate(link?.path)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-gray-600 hover:text-teal-700 transition-colors text-left">
                      <Icon name={link?.icon} size={14} color="currentColor" />
                      <span className="text-xs font-caption">{link?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <BulkActionsBar selectedCount={selectedIds?.length} onBulkAction={handleBulkAction} onClearSelection={() => setSelectedIds([])} />

      {confirmModal && (
        <ActionConfirmModal action={confirmModal?.action} user={confirmModal?.user} selectedCount={selectedIds?.length} onConfirm={handleConfirm} onCancel={() => setConfirmModal(null)} />
      )}

      {showAddModal && (
        <AddUserModal onClose={() => setShowAddModal(false)} onAdd={handleAddUser} />
      )}

      {toast && (
        <div className={`fixed top-5 right-5 z-[300] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-caption max-w-sm transition-all ${toast?.type === 'warning' ? 'bg-amber-600' : 'bg-emerald-600'}`}>
          <Icon name={toast?.type === 'warning' ? 'AlertTriangle' : 'CheckCircle'} size={16} color="white" />
          {toast?.message}
          <button onClick={() => setToast(null)} className="ml-auto"><Icon name="X" size={14} color="white" /></button>
        </div>
      )}
    </div>
  );
};

export default UserManagement;